import { YStack, XStack, Text, styled } from 'tamagui'
import { Card, GradientText } from '../ui'
import { copy } from '../../content/copy'

const Section = styled(YStack, {
  name: 'TransformationSection',
  paddingHorizontal: '$6',
  paddingVertical: '$16',
  backgroundColor: '$backgroundSecondary',

  '$md': {
    paddingHorizontal: '$4',
    paddingVertical: '$10',
  },
})

const Container = styled(YStack, {
  name: 'Container',
  maxWidth: 1000,
  marginHorizontal: 'auto',
  width: '100%',
  gap: '$10',
})

const Header = styled(YStack, {
  name: 'Header',
  gap: '$3',
  marginBottom: '$4',
})

const HeaderRow = styled(XStack, {
  name: 'HeaderRow',
  alignItems: 'flex-start',
  gap: '$4',
})

const SectionNumber = styled(Text, {
  name: 'SectionNumber',
  fontFamily: '$accent',
  fontSize: '$2',
  color: '$textMuted',
  letterSpacing: 2,
})

const SectionTitle = styled(Text, {
  name: 'SectionTitle',
  fontFamily: '$accent',
  fontSize: '$3',
  color: '$orangePrimary',
  letterSpacing: 2,
  textTransform: 'uppercase',
})

const MainHeader = styled(Text, {
  name: 'MainHeader',
  // @ts-ignore - Tamagui's `render` controls the underlying HTML element on web
  render: 'h2',
  fontFamily: '$heading',
  fontSize: '$8',
  fontWeight: '600',
  color: '$textPrimary',
  marginTop: '$4',

  '$md': {
    fontSize: '$7',
  },
})

const TimelineContainer = styled(YStack, {
  name: 'TimelineContainer',
  gap: '$1',
  position: 'relative',
})

const TimelineItem = styled(XStack, {
  name: 'TimelineItem',
  gap: '$6',
  alignItems: 'stretch',

  '$md': {
    flexDirection: 'column',
    gap: '$4',
  },
})

const TimelineLeft = styled(YStack, {
  name: 'TimelineLeft',
  width: 160,
  alignItems: 'flex-end',
  paddingRight: '$4',
  position: 'relative',

  '$md': {
    width: '100%',
    alignItems: 'flex-start',
    paddingRight: 0,
    flexDirection: 'row',
    gap: '$3',
  },
})

const TimelineDot = styled(YStack, {
  name: 'TimelineDot',
  width: 16,
  height: 16,
  borderRadius: 8,
  backgroundColor: '$orangePrimary',
  position: 'absolute',
  right: -8,
  top: 4,

  '$md': {
    position: 'relative',
    right: 0,
    top: 2,
  },
})

const TimelineConnector = styled(YStack, {
  name: 'TimelineConnector',
  position: 'absolute',
  right: -1,
  top: 20,
  bottom: -20,
  width: 2,
  backgroundColor: '$border',

  '$md': {
    display: 'none',
  },
})

const DashedTimelineConnector = styled(YStack, {
  name: 'DashedTimelineConnector',
  position: 'absolute',
  right: -1,
  top: 20,
  bottom: -20,
  width: 0,
  borderLeftWidth: 2,
  borderStyle: 'dashed',
  borderColor: '$border',

  '$md': {
    display: 'none',
  },
})

const StageName = styled(Text, {
  name: 'StageName',
  // @ts-ignore - Tamagui's `render` controls the underlying HTML element on web
  render: 'h3',
  fontFamily: '$accent',
  fontSize: '$5',
  fontWeight: '600',
  color: '$orangePrimary',
  textAlign: 'right',
  textTransform: 'uppercase',
  letterSpacing: 2,

  '$md': {
    textAlign: 'left',
  },
})

const StageSubtitle = styled(Text, {
  name: 'StageSubtitle',
  fontFamily: '$body',
  fontSize: '$3',
  color: '$textMuted',
  textAlign: 'right',

  '$md': {
    textAlign: 'left',
  },
})

const Timeframe = styled(Text, {
  name: 'Timeframe',
  fontFamily: '$body',
  fontSize: '$3',
  color: '$textMuted',
  textAlign: 'right',

  '$md': {
    textAlign: 'left',
  },
})

const TimelineRight = styled(YStack, {
  name: 'TimelineRight',
  flexGrow: 1,
  flexShrink: 1,
  minWidth: 0,
  paddingLeft: '$6',
  paddingBottom: '$8',

  '$md': {
    paddingLeft: 0,
    paddingBottom: '$6',
  },
})

const Tagline = styled(Text, {
  name: 'Tagline',
  fontFamily: '$body',
  fontSize: '$3',
  fontStyle: 'italic',
  color: '$textMuted',
  marginBottom: '$3',
})

const OutcomeText = styled(Text, {
  name: 'OutcomeText',
  fontFamily: '$body',
  fontSize: '$5',
  lineHeight: '$6',
  color: '$textPrimary',

  '$md': {
    fontSize: '$4',
    lineHeight: '$5',
  },
})


export function TransformationSection() {
  const { sectionNumber, sectionTitle, header, stages } = copy.transformation

  return (
    <Section>
      <Container>
        <Header>
          <HeaderRow>
            <SectionNumber>{sectionNumber}</SectionNumber>
            <SectionTitle>{sectionTitle}</SectionTitle>
          </HeaderRow>
          <MainHeader>{header}</MainHeader>
        </Header>

        <TimelineContainer>
          {stages.map((stage, index) => (
            <TimelineItem key={index}>
              <TimelineLeft>
                <YStack gap="$1" alignItems="flex-end" $md={{ alignItems: 'flex-start' }}>
                  <StageName>{stage.stage}</StageName>
                  <StageSubtitle>{stage.subtitle}</StageSubtitle>
                  <Timeframe>{stage.timeframe}</Timeframe>
                </YStack>
                <TimelineDot />
                {index < stages.length - 1 &&
                  (stages[index + 1].optional ? (
                    <DashedTimelineConnector />
                  ) : (
                    <TimelineConnector />
                  ))}
              </TimelineLeft>

              <TimelineRight>
                <Card variant={stage.emphasized ? 'highlighted' : 'subtle'}>
                  <Tagline>{stage.tagline}</Tagline>
                  <OutcomeText>{stage.outcome}</OutcomeText>
                </Card>
              </TimelineRight>
            </TimelineItem>
          ))}
        </TimelineContainer>
      </Container>
    </Section>
  )
}
